import { autoinject, computedFrom, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { VetClientModel, VetModel } from "../../../../../../models/vetting/vetting.models";
import {
    NotificationClientService,
    NotificationType
} from "../../../../../../services/basics/notification-client.service";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { I18N } from "aurelia-i18n";
import { RightshipConfirmationDialogModel } from "../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CustomerService } from "../../../../../../services/modules/customer-client.service";
import { BaseOfficeTime } from "../../../common-steps/base-office-time";
import { VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { AutoScreeningService } from "../../../../../../services/vetting/auto-screening.service";
import { type IOrderMetaPlugin } from "../../../../../../models/rightstore/rightstore.models";

type ScreeningDataModel = {
    ruleCollectionInputVM: string;
    vessel: VesselDetailModel;
    onConfirm: () => void;
    vet: VetClientModel;
    onViewAutoScreening: (id: number) => void;
    ruleCollectionId: number;
    customerId: number;
    businessUnitId: number;
    vesselId: number;
    isTest: boolean;
};

@autoinject
@transient()
export class RequestAutoScreenDialog extends BaseOfficeTime {

    viewModel: string = "default-screening";
    public data: ScreeningDataModel;
    public customerId: number;
    public autoScreenRequestSubmitted: boolean;
    
    public ishostedCustomer: boolean = false;

    vessel: VesselDetailModel;
    vetId: number;
    
    showBerthInVetRequest: boolean;
    onConfirm: () => void;
    onViewAutoScreening: (id: number) => void;
    ruleCollectionId: number;

    composeVM: any;

    @computedFrom("composeVM", "composeVM.currentViewModel")
    get metaVM(): IOrderMetaPlugin
    {
        const vm = this.composeVM && this.composeVM.currentViewModel? this.composeVM.currentViewModel: null;

        return vm;
    }
    
    constructor(private dialogClientService: DialogClientService, private vettingService: AutoScreeningService,
        private notificationService: NotificationClientService, private router: Router, private i18N: I18N,
         private customerService: CustomerService, private validationsClientService: ValidationsClientService) {
        super();
    }

    async activate(data: ScreeningDataModel) {
        this.data = data;
        this.vessel = data.vessel;
        if(this.vessel)
            this.data["vesselId"] = this.vessel.vesselId;
        this.onConfirm = data.onConfirm;
        this.onViewAutoScreening = data.onViewAutoScreening;
        this.ruleCollectionId = data.ruleCollectionId;
        if(this.data && this.data.ruleCollectionInputVM && this.data.ruleCollectionInputVM.trim() != '')
            this.viewModel = this.data.ruleCollectionInputVM;
        
        try {
            this.ishostedCustomer = await this.customerService.isHostedCustomer();
        } catch
        {
            this.ishostedCustomer = false;
        }
    }

    close() {
        if (this.onConfirm)
            this.onConfirm();

        this.dialogClientService.hide();
    }

    navigateToAutoScreen() {
        if (this.onViewAutoScreening) {
            this.onViewAutoScreening(this.vetId);
            this.dialogClientService.hide();
        } else {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.AutoScreening, { id: this.vetId, source: "vessels", forceLoad:true })
        }
    }

    submit() {
        this.metaVM.isValid().then(valid => {
            if (valid) 
            {
                const metaModel = this.metaVM.getMetaModel();
                const vet: VetModel = {
                    vesselId: this.data.vesselId,
                    portFromId: metaModel.portFromId,
                    portToId: metaModel.portToId,
                    departureDate: metaModel.departureDate,
                    arrivalDate: metaModel.arrivalDate,
                    cargoTypeId: metaModel.cargoTypeId,
                    berth: metaModel.berth,
                    vetId: metaModel.vetId,
                    businessUnitId: metaModel.businessUnitId ?? this.data.businessUnitId,
                    isTest: metaModel.isTest,
                    identifier: metaModel.identifier,
                    ruleCollectionId: metaModel.ruleCollectionId ?? this.ruleCollectionId,
                    charterTypeId: metaModel.charterTypeId,
                    timeCharterStartDate: metaModel.timeCharterStartDate,
                    timeCharterEndDate: metaModel.timeCharterEndDate
                };
                const model = new RightshipConfirmationDialogModel();
                model.confirm = this.i18N.tr('confirm-text');
                model.cancel = this.i18N.tr('back-text');
                model.messages.push("Your screening cannot be amended once you confirm.");
                model.title = "Confirm Screening request";
                model.onConfirm = () => {
                    return new Promise<void>((resolve, reject) => {
                        this.vettingService.saveAutoScreening(metaModel.manualDischargePort, metaModel.manualLoadPort, metaModel.dischargePortCountryId,
                            metaModel.loadPortCountryId, metaModel.dischargePort, metaModel.loadPort, 
                            metaModel.charterTypeId, metaModel.timeCharterStartDate, metaModel.timeCharterEndDate, vet)
                            .then(response => {
                                if (response.validationResult && response.validationResult.hasError) {
                                    this.validationsClientService.showValidationErrors(response.validationResult);
                                } else {
                                    if (response && response.generatedId > 0) {
                                        this.vetId = response.generatedId;
                                        this.autoScreenRequestSubmitted = true;
                                    } else {
                                        console.error(response);
                                    }
                                }

                                resolve();
                            })
                            .catch(response => {
                                if (response.validationResult && response.validationResult.hasError) {
                                    this.validationsClientService.showValidationErrors(response.validationResult);
                                } else if (!RequestClientService.isExceptionHandled(response)) {
                                    this.notificationService.show("Error submitting screening request", "", NotificationType.Error);
                                }
                                reject();
                            });
                    });
                }

                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, false, false);
            }
        });
    }
}
